/* eslint-disable react/display-name */
import React from 'react';
import styled, { css } from 'styled-components';
import { toMapStateToLinkProps } from '@peloton/external-links/withExtHrefs';
import { Links } from '@peloton/links';
import { orderHistoryAccount } from '@peloton/links/account';
import { hoverTransition } from '@peloton/styles/animation';
import { media } from '@peloton/styles/breakpoints';
import { track } from '@engage/analytics/actions';
import { slate2, gray2, gray3, white } from '@engage/colors/base';
import { useReduxAction, useReduxState } from '@engage/redux/hooks';
import { spaces } from '@engage/styles/spaces';
import { label12 } from '@engage/typography/font-styles';
import { link1 } from '@engage/typography/links';
import * as Analytics from '@members/analytics';
import { useLogout } from '@members/auth/OauthProvider';
import { LinksCopy, TopNavCopy } from '@members/copy/copy.generated';
import { TopNav } from '@members/copy/models.generated/TopNav';
import { BrowseTags } from '@members/icons/BrowseTags';
import { FindMembers } from '@members/icons/FindMembers';
import GiftIcon from '@members/icons/GiftIcon';
import { LogOut } from '@members/icons/LogOut';
import { Settings as SettingsIcon } from '@members/icons/Settings';
import { ShoppingCart } from '@members/icons/ShoppingCart';
import Link from '@members/layout/nav/NavLink';
import { useMembersRouter } from '@members/localization/useMembersRouter';
import MemberSearchModal from '@members/member-search/MemberSearchModal';
import {
  toDeviceNameId,
  toDownloadAppsProps,
  useMaybeAppDownload,
} from '@members/modal-get-app/copy';
import { useGetApps } from '@members/modal-get-app/useGetApps';
import { PREFS_ROUTE } from '@members/pg-preferences/urls';
import { useReferralsUrlWithSource } from '@members/referrals/hooks';
import { TagModalView } from '@members/tags/shared';
import { toTagsModalQuery } from '@members/tags/urls';
import { useIsFreemiumMember } from '@members/tiering/hooks/useIsFreemiumMember';
import { navMainLink } from '../../../sharedLinkStyles';
import { MENU_SOURCE, MENU_ANALYTICS_OPTIONS } from '../../analytics';
import {
  openMemberSearchFromNav,
  SEARCH_MODAL_NAME_FROM_NAV,
} from '../MemberSearchFromNav';

export const MiniNav = React.forwardRef<HTMLUListElement, MiniNavViewProps>(
  ({ isMenuOpen, handleMenuClose, handleOpenGetApp }, ref) => {
    const deviceId = toDeviceNameId();
    const device = useMaybeAppDownload(deviceId);
    const hasGetApps = useGetApps();
    const router = useMembersRouter();

    const links = useReduxState(
      toMapStateToLinkProps({
        boutiqueHref: Links.boutique,
        supportHref: Links.support,
        orderhistoryHref: Links.orderhistory,
        orderhistoryAccountHref: orderHistoryAccount,
      }),
    );

    const trackAction = useReduxAction(track);
    const handleSearchClick = useReduxAction(openMemberSearchFromNav);

    const referralsUrlWithSource = useReferralsUrlWithSource('nav');

    const { stringifiedTagsQueryParams, tagsQueryParams } = toTagsModalQuery(
      TagModalView.BrowseTags,
    );

    const goToBrowseTags = () =>
      router.push(
        {
          query: { ...router.query, ...tagsQueryParams, source: MENU_SOURCE },
        },
        stringifiedTagsQueryParams,
        { shallow: true },
      );
    const logout = useLogout();

    const onClick = (cb: () => void) => () => {
      handleMenuClose();
      cb();
    };

    const isFreemiumMember = useIsFreemiumMember();

    useKeyboardNavigation(isMenuOpen, handleMenuClose);
    return (
      <Container
        role="menu"
        data-test-id="miniNav"
        id="miniNav"
        ref={ref}
        isMenuOpen={isMenuOpen}
        as="ul"
      >
        <li role="none">
          <SearchButton onClick={onClick(handleSearchClick)} role="menuitem">
            <StyledFindMembersIcon />
            <LinkText data-test-id="findMembersButton">
              <TopNavCopy id={TopNav.FindMembers} />
            </LinkText>
          </SearchButton>
        </li>
        {!isFreemiumMember ? (
          <li role="none">
            <MiniNavLink
              data-test-id="referFriendsButton"
              role="menuitem"
              href={referralsUrlWithSource}
              target="_blank"
              rel="noopener"
            >
              <StyledGiftIcon />
              <LinkText>
                <TopNavCopy id={TopNav.ReferFriends} />
              </LinkText>
            </MiniNavLink>
          </li>
        ) : null}
        <li role="none">
          <StyledButton onClick={onClick(goToBrowseTags)} role="menuitem">
            <StyledBrowseTags />
            <LinkText data-test-id="exploreTagsNavButton">
              <TopNavCopy id={TopNav.ExploreTags} />
            </LinkText>
          </StyledButton>
        </li>
        <li>
          <MiniNavRouterLink href={PREFS_ROUTE} role="menuitem">
            <StyledSettingsIcon />
            <LinkText data-test-id="accountLink">
              <TopNavCopy id={TopNav.Preferences} />
            </LinkText>
          </MiniNavRouterLink>
        </li>
        <li role="none">
          <MiniNavLink
            href={links.orderhistoryAccountHref}
            onClick={onClick(() => trackAction(Analytics.trackClickOrderHistory()))}
            data-test-id="orderHistory"
            target="_blank"
            rel="noopener"
            role="menuitem"
          >
            <StyledOrderHistoryIcon />
            <LinkText>
              <TopNavCopy id={TopNav.OrderHistory} />
            </LinkText>
          </MiniNavLink>
        </li>
        <li role="none">
          <StyledButton
            onClick={onClick(() => logout(MENU_ANALYTICS_OPTIONS))}
            role="menuitem"
          >
            <StyledLogoutIcon />
            <LinkText data-test-id="logoutButton">
              <TopNavCopy id={TopNav.Logout} />
            </LinkText>
          </StyledButton>
        </li>
        <li role="none">
          <SecondaryLinksContainer role="group">
            {hasGetApps ? (
              <li role="none">
                <SecondaryButton onClick={onClick(handleOpenGetApp)} role="menuitem">
                  <LinksCopy {...toDownloadAppsProps(device)} />
                </SecondaryButton>
              </li>
            ) : null}
            <li role="none">
              <SecondaryLink
                href={links.boutiqueHref}
                onClick={onClick(() =>
                  trackAction(Analytics.trackClickBoutique(MENU_SOURCE)),
                )}
                data-test-id="boutiqueLink"
                target="_blank"
                rel="noreferrer"
                role="menuitem"
              >
                <TopNavCopy id={TopNav.PelotonApparel} />
              </SecondaryLink>
            </li>
            <li role="none">
              <SecondaryLink
                href={links.supportHref}
                onClick={onClick(() =>
                  trackAction(Analytics.trackClickSupport(MENU_SOURCE)),
                )}
                data-test-id="supportLink"
                target="_blank"
                rel="noreferrer"
                role="menuitem"
              >
                <TopNavCopy id={TopNav.Support} />
              </SecondaryLink>
            </li>
          </SecondaryLinksContainer>
        </li>
        <MemberSearchModal modalName={SEARCH_MODAL_NAME_FROM_NAV} />
      </Container>
    );
  },
);

const useKeyboardNavigation = (isOpen: boolean, onClose: () => void) => {
  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (
        (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
        e.target instanceof HTMLElement &&
        e.target.closest('#miniNav')
      ) {
        e.preventDefault();
        const menuItems = Array.from(
          document.querySelectorAll('#miniNav [role="menuitem"]'),
        );
        const currentIndex = menuItems.findIndex(item => item === document.activeElement);

        const nextIndex =
          e.key === 'ArrowDown'
            ? (currentIndex + 1) % menuItems.length
            : (currentIndex - 1 + menuItems.length) % menuItems.length;

        (menuItems[nextIndex] as HTMLElement).focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
};
const MiniNavItemStyles = css`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  height: 46px;
  padding: 0 ${spaces.large}px;
  ${hoverTransition({
    property: 'background-color',
    from: white,
    to: gray2,
  })}
`;

const MiniNavLink = styled.a`
  ${MiniNavItemStyles}
`;

const SearchButton = styled.button`
  border-radius: ${spaces.xxxSmall}px ${spaces.xxxSmall}px 0px 0px;
  ${MiniNavItemStyles}
  cursor: pointer;
  width: 100%;
`;

const StyledButton = styled.button`
  ${MiniNavItemStyles}
  cursor: pointer;
  width: 100%;
`;

const MiniNavRouterLink = styled(Link)`
  ${MiniNavItemStyles}
`;

const SecondaryLinksContainer = styled.ul`
  border-radius: 0px 0px ${spaces.xxxSmall}px ${spaces.xxxSmall}px;
  background-color: ${white};
  border-top: 1px solid ${gray3};
  display: flex;
  flex-direction: column;
  padding: 15px ${spaces.large}px;
`;

const secondaryLinkStyles = css`
  ${label12}
  color: ${slate2};
  display: block;
  padding: 5px 0;
  ${link1}
`;

const SecondaryLink = styled.a`
  ${secondaryLinkStyles}
`;

const SecondaryButton = styled.button`
  ${secondaryLinkStyles}
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
`;

const iconStyle = css`
  fill: ${slate2};
  width: ${spaces.large}px;
  margin-right: 10px;
`;

const LinkText = styled.span`
  ${navMainLink}
  color: ${slate2};
`;

const Container = styled.ul<{ isMenuOpen: boolean }>`
  border-radius: ${spaces.xxxSmall}px;
  filter: drop-shadow(0px 4px 12px rgba(0, 0, 0, 0.12));
  position: absolute;
  right: 10px;
  top: 51px;
  width: 280px;

  ${media.desktop`
    right: 56px;
  `}

  ${p => !p.isMenuOpen && `display: none;`}
`;

const StyledSettingsIcon = styled(SettingsIcon)`
  ${iconStyle}
`;

const StyledLogoutIcon = styled(LogOut)`
  fill: ${slate2};
  width: ${spaces.large}px;
  margin-right: ${spaces.xSmall}px;
  margin-left: ${spaces.tiny}px;
`;

const StyledOrderHistoryIcon = styled(ShoppingCart)`
  ${iconStyle}
`;

const StyledFindMembersIcon = styled(FindMembers)`
  ${iconStyle}
`;

const StyledBrowseTags = styled(BrowseTags)`
  ${iconStyle}
`;

const StyledGiftIcon = styled(GiftIcon)`
  ${iconStyle}
`;

export default MiniNav;

type MiniNavViewProps = {
  handleOpenGetApp(): void;
  handleMenuClose(e?: Event): void;
  isMenuOpen: boolean;
};
